import { useCallback, useRef, useState } from "react";
import axios from "axios";
import ReactCrop, { type PercentCrop, type PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import { GET_BASE_URL } from "../constants/apiEndpoints";
import { fetchMyCollection, fetchStickerLounges } from "../redux/lounges/slice";
import { useAppDispatch } from "../redux/store";
import { autoTrimTransparentEdges, cropToRect } from "../utils/imageProcessing";
import {
  buildStickerFairyPrompt,
  type StickerFairyPromptMode,
} from "../utils/stickerFairyPrompt";
import {
  buildStickerFairyFunctionUrl,
  buildStickerFairyRequestHeaders,
  STICKER_FAIRY_IMAGE_FUNCTION,
  STICKER_FAIRY_TEXT_FUNCTION,
} from "../utils/stickerFairyService";
import {
  movePercentCrop,
  normalizePercentCrop,
  percentCropToPixelCrop,
  resizePercentCrop,
  STICKER_FAIRY_DEFAULT_CROP,
} from "../utils/cropControls";
import {
  UNKNOWN_CREDIT_RETRY_MESSAGE,
  isKnownNumericCreditBalance,
} from "../utils/loungeSession";
import { buildLoungeAuthHeaders } from "../utils/loungeAuthHeader";

const STICKER_FAIRY_GENERATION_COST = 20;
const STICKER_FAIRY_CREDIT_CHARGE_CALLS = 2;
const STICKER_FAIRY_COLLECTION_ADD_COST = 10;
type StickerGenerationResult = {
  original: string;
  transparent: string;
};

type StickerFairyQuickCreateProps = {
  stickerData: any[];
  onStickerReady: (productImage: string, item?: any) => void;
};

const getCreditBalanceFromCollection = (collection: any) => {
  const item = Array.isArray(collection) ? collection[0] : collection;
  return isKnownNumericCreditBalance(item?.credit_balance)
    ? item.credit_balance
    : null;
};

export const hasStickerFairyAccessInTabs = (tabs: any[]) =>
  Array.isArray(tabs) &&
  tabs.some((tab) =>
    String(tab?.emoji_category_name || "")
      .toLowerCase()
      .includes("sticker fairy")
  );

export const findStickerFairyStickerByProductId = (tabs: any[], productId: string) => {
  for (const tab of tabs || []) {
    if (
      !String(tab?.emoji_category_name || "")
        .toLowerCase()
        .includes("sticker fairy")
    ) {
      continue;
    }

    const sticker = (tab?.getemojidata || []).find(
      (item: any) => String(item?.product_id || "") === String(productId)
    );
    if (sticker) {
      return sticker;
    }
  }

  return null;
};

export const buildStickerFairyInsertedSticker = (
  productId: string,
  productName: string,
  productImage: string
) => ({
  product_id: productId,
  product_name: productName,
  product_image: productImage,
});

export const getInsertableStickerFairyProductImage = (
  catalogSticker?: any,
  responseProductImage = ""
) => {
  const productImage = String(
    catalogSticker?.product_image || responseProductImage || ""
  ).trim();

  if (!productImage || /^data:image\//i.test(productImage)) {
    return "";
  }

  return productImage;
};

const waitForStickerCatalog = () =>
  new Promise((resolve) => window.setTimeout(resolve, 250));

const generateRandomStickerProductId = () =>
  String(Math.floor(Math.random() * 90000) + 10000);

const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as any;
    return (
      (typeof data === "string" && data) ||
      data?.message ||
      data?.error ||
      error.message ||
      fallback
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const buildProductName = (prompt: string) => {
  const normalized = prompt.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return "Sticker Fairy Creation";
  }

  return normalized.length > 72 ? `${normalized.slice(0, 69)}...` : normalized;
};

const generateThumbnail = (imageUrl: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Canvas context not available."));
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const maxHeight = 150;
      const scale = maxHeight / img.height;
      const targetHeight = maxHeight;
      const targetWidth = Math.max(1, Math.round(img.width * scale));

      canvas.width = targetWidth;
      canvas.height = targetHeight;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.clearRect(0, 0, targetWidth, targetHeight);
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Sticker preview could not be loaded."));
    img.src = imageUrl;
  });

const imageHasTransparentPixels = (imageUrl: string): Promise<boolean> =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(false);
        return;
      }

      const sampleWidth = Math.min(160, img.naturalWidth || img.width);
      const sampleHeight = Math.min(160, img.naturalHeight || img.height);
      if (!sampleWidth || !sampleHeight) {
        resolve(false);
        return;
      }

      canvas.width = sampleWidth;
      canvas.height = sampleHeight;
      ctx.drawImage(img, 0, 0, sampleWidth, sampleHeight);

      try {
        const pixels = ctx.getImageData(0, 0, sampleWidth, sampleHeight).data;
        for (let index = 3; index < pixels.length; index += 4) {
          if (pixels[index] < 250) {
            resolve(true);
            return;
          }
        }
      } catch {
        resolve(true);
        return;
      }

      resolve(false);
    };
    img.onerror = () => resolve(false);
    img.src = imageUrl;
  });

const extractGenerationResult = (payload: any): StickerGenerationResult => {
  if (!payload || !Array.isArray(payload.output) || !payload.output[0]) {
    const serverDetail =
      (typeof payload === "string" && payload) ||
      payload?.error ||
      payload?.message ||
      payload?.detail ||
      payload?.geminiError ||
      (typeof payload?.geminiResponse === "string"
        ? payload.geminiResponse
        : payload?.geminiResponse?.error?.message) ||
      "No image generated.";
    throw new Error(String(serverDetail));
  }

  return {
    original: payload.output[0],
    transparent: payload.output[1] || payload.output[0],
  };
};

const generateTextSticker = async (prompt: string, mode: StickerFairyPromptMode) => {
  const response = await axios.post(
    buildStickerFairyFunctionUrl(STICKER_FAIRY_TEXT_FUNCTION),
    { prompt: buildStickerFairyPrompt(prompt, mode) },
    {
      headers: buildStickerFairyRequestHeaders({ contentType: "application/json" }),
    }
  );

  return extractGenerationResult(response.data);
};

const generateImageSticker = async (
  uploadedImage: File,
  prompt: string,
  mode: StickerFairyPromptMode
) => {
  const formData = new FormData();
  formData.append("uploadedImage", uploadedImage, uploadedImage.name);
  formData.append("prompt", buildStickerFairyPrompt(prompt, mode));

  const response = await axios.post(
    buildStickerFairyFunctionUrl(STICKER_FAIRY_IMAGE_FUNCTION),
    formData,
    {
      headers: buildStickerFairyRequestHeaders(),
      timeout: 150000,
    }
  );

  return extractGenerationResult(response.data);
};

const StickerFairyQuickCreate: React.FC<StickerFairyQuickCreateProps> = ({
  stickerData,
  onStickerReady,
}) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cropImageRef = useRef<HTMLImageElement | null>(null);
  const [prompt, setPrompt] = useState("");
  const [promptMode, setPromptMode] = useState<StickerFairyPromptMode>("sticker");
  const [sourceImage, setSourceImage] = useState<File | null>(null);
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [originalUrl, setOriginalUrl] = useState("");
  const [transparentUrl, setTransparentUrl] = useState("");
  const [baseTransparentUrl, setBaseTransparentUrl] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [hasTransparentSticker, setHasTransparentSticker] = useState(false);
  const [crop, setCrop] = useState<PercentCrop | undefined>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const clearSourceImage = () => {
    setSourceImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const updateStickerPreview = useCallback(async (url: string) => {
    let finalUrl = url;
    try {
      finalUrl = await autoTrimTransparentEdges(url);
    } catch (error) {
      console.warn("Sticker Fairy quick trim failed:", error);
    }

    setBaseTransparentUrl(finalUrl);
    setTransparentUrl(finalUrl);
    setHasTransparentSticker(await imageHasTransparentPixels(finalUrl));
    setThumbnailUrl(await generateThumbnail(finalUrl));
  }, []);

  const chargeGenerationCredits = async (balance: number | null) => {
    if (!isKnownNumericCreditBalance(balance)) {
      throw new Error(UNKNOWN_CREDIT_RETRY_MESSAGE);
    }
    if (balance < STICKER_FAIRY_GENERATION_COST) {
      throw new Error(
        `Insufficient credits. You need at least ${STICKER_FAIRY_GENERATION_COST} credits to generate a sticker.`
      );
    }

    const token = localStorage.getItem("token");
    for (let chargeIndex = 0; chargeIndex < STICKER_FAIRY_CREDIT_CHARGE_CALLS; chargeIndex += 1) {
      const response = await fetch(GET_BASE_URL + "/api/v1/processStickerGen", {
        method: "POST",
        headers: {
          ...buildLoungeAuthHeaders(token),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error("Credit charge failed. Please try again.");
      }
    }
  };

  const saveGenerationToHistory = async (item: StickerGenerationResult, sourcePrompt: string) => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }

    await fetch(GET_BASE_URL + "/api/v1/saveStickerFairyGeneration", {
      method: "POST",
      headers: {
        ...buildLoungeAuthHeaders(token),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        prompt: sourcePrompt,
        original_image: item.original,
        transparent_image: item.transparent,
      }),
    });
  };

  const handleGenerate = async () => {
    const nextPrompt = prompt.trim();
    if (!nextPrompt) {
      setErrorMessage("Describe the sticker you want to make.");
      return;
    }

    setIsGenerating(true);
    setMessage("");
    setErrorMessage("");
    setOriginalUrl("");
    setTransparentUrl("");
    setBaseTransparentUrl("");
    setThumbnailUrl("");
    setIsCropping(false);

    try {
      const collection = await dispatch(fetchMyCollection()).unwrap();
      const balance = getCreditBalanceFromCollection(collection);
      const result = sourceImage
        ? await generateImageSticker(sourceImage, nextPrompt, promptMode)
        : await generateTextSticker(nextPrompt, promptMode);

      await chargeGenerationCredits(balance);
      const visibleStickerUrl = result.transparent || result.original;
      const nextResult = {
        original: result.original || visibleStickerUrl,
        transparent: visibleStickerUrl,
      };

      setOriginalUrl(nextResult.original);
      setGeneratedPrompt(nextPrompt);
      await updateStickerPreview(nextResult.transparent);
      await saveGenerationToHistory(nextResult, nextPrompt);
      setPrompt("");
      clearSourceImage();
      void dispatch(fetchMyCollection());
      setMessage("Sticker created. Crop it if needed, then add it to your collection.");
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, "Sticker generation failed."));
    } finally {
      setIsGenerating(false);
    }
  };

  const updateCropSelection = useCallback((nextCrop: PercentCrop) => {
    const normalizedCrop = normalizePercentCrop(nextCrop);
    setCrop(normalizedCrop);
    const image = cropImageRef.current;
    setCompletedCrop(
      image ? percentCropToPixelCrop(normalizedCrop, image.width, image.height) : null
    );
  }, []);

  const onCropChange = (_pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
    updateCropSelection(percentCrop);
  };

  const onCropComplete = (pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
    setCrop(normalizePercentCrop(percentCrop));
    setCompletedCrop(pixelCrop.width && pixelCrop.height ? pixelCrop : null);
  };

  const handleApplyCrop = async () => {
    const activeCrop =
      completedCrop ||
      (crop && cropImageRef.current
        ? percentCropToPixelCrop(crop, cropImageRef.current.width, cropImageRef.current.height)
        : null);

    if (!activeCrop || activeCrop.width <= 0 || activeCrop.height <= 0) {
      setErrorMessage("Select an area to crop.");
      return;
    }

    try {
      const croppedUrl = await cropToRect(transparentUrl, activeCrop, crop);
      setTransparentUrl(croppedUrl);
      setHasTransparentSticker(await imageHasTransparentPixels(croppedUrl));
      setThumbnailUrl(await generateThumbnail(croppedUrl));
      setIsCropping(false);
      setMessage("Crop applied.");
    } catch (error) {
      setErrorMessage("Cropping unavailable for this image.");
    }
  };

  const handleResetCrop = async () => {
    if (!baseTransparentUrl) {
      return;
    }

    setIsCropping(false);
    await updateStickerPreview(baseTransparentUrl);
    setMessage("Crop reset.");
  };

  const handleAddToCollection = async () => {
    if (!thumbnailUrl || !generatedPrompt) {
      setErrorMessage("Create a sticker before adding it.");
      return;
    }

    setIsAdding(true);
    setMessage("");
    setErrorMessage("");

    const token = localStorage.getItem("token");
    const productId = generateRandomStickerProductId();
    const productName = buildProductName(generatedPrompt);

    try {
      const addResponse = await axios.post(
        GET_BASE_URL + "/api/v1/addStickerFairytoCollection",
        {
          product_id: productId,
          product_name: productName,
          product_image: thumbnailUrl,
        },
        {
          headers: {
            ...buildLoungeAuthHeaders(token),
            "Content-Type": "application/json",
          },
        }
      );

      if (String(addResponse.data?.status || "") !== "200") {
        throw new Error(
          addResponse.data?.message || "Could not add this sticker to your collection."
        );
      }

      void dispatch(fetchMyCollection());
      const responseProductImage = String(
        addResponse.data?.product_image || addResponse.data?.data?.product_image || ""
      ).trim();
      let refreshedTabs: any[] = [];
      let newSticker: any;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        refreshedTabs = await dispatch(fetchStickerLounges({ emojiData: [] })).unwrap();
        newSticker = findStickerFairyStickerByProductId(refreshedTabs, productId);
        if (newSticker) {
          break;
        }
        await waitForStickerCatalog();
      }

      const insertedProductImage = getInsertableStickerFairyProductImage(
        newSticker,
        responseProductImage
      );

      if (!insertedProductImage) {
        setMessage(
          "Added to your collection. Reopen the Sticker Fairy tab if it does not appear yet."
        );
        console.warn(
          "Sticker Fairy quick-created sticker did not have an insertable saved product image.",
          { productId, responseProductImage }
        );
        return;
      }

      const insertedSticker =
        newSticker ||
        buildStickerFairyInsertedSticker(productId, productName, insertedProductImage);
      onStickerReady(insertedProductImage, insertedSticker);
      setMessage("Added to your collection and inserted into your post.");
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, "Could not add this sticker to your collection."));
    } finally {
      setIsAdding(false);
    }
  };

  if (!hasStickerFairyAccessInTabs(stickerData)) {
    return null;
  }

  return (
    <div className="sticker-fairy-quick-create" aria-label="Create a Sticker Fairy sticker">
      <div className="sticker-fairy-mode-toggle" role="group" aria-label="Sticker Fairy mode">
        <button
          type="button"
          className={promptMode === "sticker" ? "active" : ""}
          aria-pressed={promptMode === "sticker"}
          onClick={() => setPromptMode("sticker")}
        >
          Sticker
        </button>
        <button
          type="button"
          className={promptMode === "art" ? "active" : ""}
          aria-pressed={promptMode === "art"}
          onClick={() => setPromptMode("art")}
        >
          Art
        </button>
      </div>
      <textarea
        value={prompt}
        onChange={(event) => setPrompt(event.target.value)}
        placeholder={promptMode === "sticker" ? "Describe the sticker..." : "Describe the art..."}
        rows={3}
      />
      <div className="sticker-fairy-quick-create__upload">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(event) => setSourceImage(event.target.files?.[0] || null)}
          aria-label="Add source image"
        />
        {sourceImage && (
          <button type="button" onClick={clearSourceImage}>
            Remove image
          </button>
        )}
      </div>
      <button type="button" onClick={handleGenerate} disabled={isGenerating || isAdding}>
        {isGenerating
          ? "Generating..."
          : `Generate ${promptMode === "sticker" ? "Sticker" : "Art"} (${STICKER_FAIRY_GENERATION_COST} credits)`}
      </button>

      {transparentUrl && !isGenerating && (
        <div className="sticker-fairy-quick-create__result">
          <img src={thumbnailUrl || transparentUrl} alt="Generated Sticker Fairy preview" />
          <div className="sticker-fairy-quick-create__actions">
            <button
              type="button"
              onClick={() => {
                setCrop(STICKER_FAIRY_DEFAULT_CROP);
                setCompletedCrop(null);
                setIsCropping(true);
              }}
            >
              Edit Crop
            </button>
            {baseTransparentUrl && (
              <button type="button" onClick={handleResetCrop}>
                Reset Crop
              </button>
            )}
          </div>
          {!hasTransparentSticker && (
            <p className="sticker-fairy-quick-create__hint">
              Crop this sticker so it fills the Lounge sticker area.
            </p>
          )}
          {isCropping && (
            <div className="sticker-fairy-quick-create__crop">
              <ReactCrop
                crop={crop}
                onChange={onCropChange}
                onComplete={onCropComplete}
                keepSelection
              >
                <img
                  ref={cropImageRef}
                  src={transparentUrl}
                  alt="Crop Sticker Fairy sticker"
                  onLoad={() => updateCropSelection(crop || STICKER_FAIRY_DEFAULT_CROP)}
                />
              </ReactCrop>
              <div className="sticker-fairy-quick-create__crop-controls">
                <button type="button" onClick={() => updateCropSelection(movePercentCrop(crop, 0, -4))} aria-label="Move crop up">
                  ↑
                </button>
                <button type="button" onClick={() => updateCropSelection(movePercentCrop(crop, -4, 0))} aria-label="Move crop left">
                  ←
                </button>
                <button type="button" onClick={() => updateCropSelection(movePercentCrop(crop, 4, 0))} aria-label="Move crop right">
                  →
                </button>
                <button type="button" onClick={() => updateCropSelection(movePercentCrop(crop, 0, 4))} aria-label="Move crop down">
                  ↓
                </button>
                <button type="button" onClick={() => updateCropSelection(resizePercentCrop(crop, -6))}>
                  Smaller
                </button>
                <button type="button" onClick={() => updateCropSelection(resizePercentCrop(crop, 6))}>
                  Larger
                </button>
              </div>
              <div className="sticker-fairy-quick-create__actions">
                <button type="button" onClick={handleApplyCrop}>
                  Apply Crop
                </button>
                <button type="button" onClick={() => setIsCropping(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          <button
            type="button"
            className="sticker-fairy-quick-create__add"
            onClick={handleAddToCollection}
            disabled={isAdding || !thumbnailUrl}
          >
            {isAdding
              ? "Adding..."
              : `Add to your collection and insert your sticker (${STICKER_FAIRY_COLLECTION_ADD_COST} credits)`}
          </button>
        </div>
      )}

      {message && <p className="feedback feedback--success">{message}</p>}
      {errorMessage && <p className="feedback feedback--error">{errorMessage}</p>}
    </div>
  );
};

export default StickerFairyQuickCreate;
